/**
 * Component Health and Lifecycle Management Types
 * 
 * Defines interfaces for independent component management,
 * health monitoring, and decoupled event systems.
 */

export enum ComponentStatus {
  INITIALIZING = 'initializing',
  HEALTHY = 'healthy', 
  DEGRADED = 'degraded',
  FAILED = 'failed',
  RECOVERING = 'recovering',
  SHUTDOWN = 'shutdown'
}

export enum ComponentType {
  STT_SERVICE = 'stt_service',
  LLM_SERVICE = 'llm_service', 
  TTS_SERVICE = 'tts_service',
  WEBRTC_SERVICE = 'webrtc_service',
  WEBSOCKET_SERVICE = 'websocket_service',
  SESSION_MANAGER = 'session_manager',
  TURN_MANAGER = 'turn_manager',
  PARTICIPANT_MANAGER = 'participant_manager'
}

export interface ComponentHealth {
  componentId: string;
  componentType: ComponentType;
  status: ComponentStatus;
  lastHealthCheck: number; // timestamp
  errorCount: number;
  uptime: number; // milliseconds
  metadata?: Record<string, any>;
  dependencies: string[]; // component IDs this depends on
}

export interface ComponentEvent {
  eventId: string;
  sourceComponentId: string;
  eventType: string;
  timestamp: number;
  data: Record<string, any>;
  targetComponents?: ComponentType[]; // if undefined, broadcast to all
  priority: 'low' | 'normal' | 'high' | 'critical';
}

export interface ComponentEventFilter {
  componentType: ComponentType;
  eventTypes: string[];
  sourceComponents?: ComponentType[];
  handler: (event: ComponentEvent) => Promise<void> | void;
}

/**
 * Circuit Breaker States for LLM and other external services
 */
export enum CircuitBreakerState {
  CLOSED = 'closed',     // Normal operation
  OPEN = 'open',         // Failures detected, requests blocked
  HALF_OPEN = 'half_open' // Testing if service recovered
}

export interface CircuitBreakerConfig {
  failureThreshold: number;     // failures before opening
  recoveryTimeoutMs: number;    // time to wait before testing recovery
  successThreshold: number;     // successes needed to close circuit
  timeWindowMs: number;         // window for counting failures
}

export interface CircuitBreakerStatus {
  state: CircuitBreakerState;
  failureCount: number;
  successCount: number;
  lastFailureTime: number;
  nextRetryTime: number;
}

/**
 * Cleanup coordination interfaces
 */
export enum CleanupPhase {
  PREPARE = 'prepare',     // Notify components of impending cleanup
  GRACEFUL = 'graceful',   // Allow components time to finish operations
  FORCE = 'force',         // Force cleanup if graceful fails
  COMPLETE = 'complete'    // Cleanup finished
}

export interface CleanupOperation {
  operationId: string;
  roomId: string;
  phase: CleanupPhase;
  targetComponents: ComponentType[];
  timeoutMs: number;
  startTime: number;
}

export interface CleanupResult {
  componentId: string;
  success: boolean;
  durationMs: number;
  error?: string;
}

/**
 * Service interfaces for independent component management
 */
export interface IComponentHealthMonitor {
  registerComponent(health: ComponentHealth): Promise<void>;
  updateComponentHealth(componentId: string, status: ComponentStatus, metadata?: Record<string, any>): Promise<void>;
  getComponentHealth(componentId: string): Promise<ComponentHealth | null>;
  getAllComponentHealth(roomId: string): Promise<ComponentHealth[]>;
  removeComponent(componentId: string): Promise<void>;
}

export interface IComponentEventBus {
  subscribe(filter: ComponentEventFilter): Promise<string>; // returns subscription ID
  unsubscribe(subscriptionId: string): Promise<void>;
  publish(event: ComponentEvent): Promise<void>;
  getEventHistory(componentId: string, limit?: number): Promise<ComponentEvent[]>;
}

export interface ICircuitBreaker {
  execute<T>(operation: () => Promise<T>): Promise<T>;
  getStatus(): CircuitBreakerStatus;
  reset(): void;
  forceOpen(): void;
  forceClose(): void;
}

export interface ICleanupOrchestrator {
  initiateCleanup(roomId: string, targetComponents?: ComponentType[]): Promise<string>; // returns operation ID
  getCleanupStatus(operationId: string): Promise<CleanupOperation | null>;
  cancelCleanup(operationId: string): Promise<boolean>;
}

/**
 * Component interface that all managed components should implement
 */
export interface IManagedComponent {
  readonly componentId: string;
  readonly componentType: ComponentType;
  
  initialize(): Promise<void>;
  healthCheck(): Promise<ComponentHealth>;
  gracefulShutdown(timeoutMs: number): Promise<void>;
  forceShutdown(): Promise<void>;
  
  // Event handling
  onEvent(event: ComponentEvent): Promise<void> | void;
}

/**
 * Events that components can emit
 */
export const ComponentEvents = {
  // Health events
  HEALTH_CHECK: 'health_check',
  COMPONENT_FAILED: 'component_failed',
  COMPONENT_RECOVERED: 'component_recovered',
  
  // Lifecycle events  
  COMPONENT_STARTING: 'component_starting',
  COMPONENT_READY: 'component_ready',
  COMPONENT_SHUTTING_DOWN: 'component_shutting_down',
  COMPONENT_SHUTDOWN: 'component_shutdown',
  
  // STT specific events
  STT_SESSION_STARTED: 'stt_session_started',
  STT_SESSION_ENDED: 'stt_session_ended',
  STT_CONNECTION_FAILED: 'stt_connection_failed',
  STT_TRANSCRIPTION_RECEIVED: 'stt_transcription_received',
  
  // LLM specific events
  LLM_REQUEST_STARTED: 'llm_request_started',
  LLM_REQUEST_COMPLETED: 'llm_request_completed',
  LLM_REQUEST_FAILED: 'llm_request_failed',
  LLM_PROVIDER_SWITCHED: 'llm_provider_switched',
  
  // Session events (non-breaking)
  SESSION_USER_JOINED: 'session_user_joined',
  SESSION_USER_LEFT: 'session_user_left',
  SESSION_STATE_CHANGED: 'session_state_changed',
  
  // WebRTC events
  WEBRTC_CONNECTED: 'webrtc_connected',
  WEBRTC_DISCONNECTED: 'webrtc_disconnected',
  WEBRTC_FAILED: 'webrtc_failed',
  
  // Cleanup events
  CLEANUP_COMPLETED: 'cleanup_completed',
  CLEANUP_STT_SERVICE: 'cleanup_stt_service',
  CLEANUP_TTS_SERVICE: 'cleanup_tts_service',
  CLEANUP_WEBRTC_SERVICE: 'cleanup_webrtc_service',
  CLEANUP_SESSION_MANAGER: 'cleanup_session_manager',
  CLEANUP_LLM_SERVICE: 'cleanup_llm_service'
} as const;

export type ComponentEventType = typeof ComponentEvents[keyof typeof ComponentEvents];